import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, Plus, Clock, CheckCircle, BarChart2, Copy, Loader, AlertCircle, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPORTS = ["tennis", "pickleball", "golf", "padel", "squash", "badminton"];
const SESSION_TYPES = [
  { value: "open_play", label: "Open Play" },
  { value: "doubles", label: "Doubles Match" },
  { value: "practice", label: "Practice / Drill" },
  { value: "mixer", label: "Skill-Matched Mixer" },
  { value: "ladder_lite", label: "Ladder-Lite" },
];
const SKILLS = ["beginner", "intermediate", "advanced", "expert"];

// ── Organizer Application Form ─────────────────────────────────────────────
function OrganizerApply({ onApplied }) {
  const [form, setForm] = useState({ bio: "", sports: [], city: "Fort Worth, TX", experience: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const toggleSport = (s) => setForm((p) => ({
    ...p,
    sports: p.sports.includes(s) ? p.sports.filter(x => x !== s) : [...p.sports, s],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bio || !form.experience || form.sports.length === 0) { setError("Please fill all fields."); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/organizers/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (r.ok) { onApplied(); }
      else { const d = await r.json(); setError(d.detail || "Application failed"); }
    } catch { setError("Network error. Try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-heading font-black text-white mb-2">Become an Organizer</h1>
        <p className="text-gray-400">Create and manage skill-matched sessions in your community.</p>
      </div>
      <form onSubmit={submit} className="space-y-5 bg-[#161B28] border border-white/8 rounded-2xl p-6 dark-section">
        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3" role="alert">
            <AlertCircle size={14} /> {error}
          </div>
        )}
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5" htmlFor="bio">Your Bio</label>
          <textarea id="bio" rows={3} required value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
            placeholder="Tell players about yourself…"
            className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green/40 resize-none dark-section"
            data-testid="organizer-bio-input" />
        </div>
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Sports You Organise</label>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Sport selection">
            {SPORTS.map(s => (
              <button key={s} type="button" onClick={() => toggleSport(s)}
                className={`px-3 py-1.5 rounded-lg text-xs border capitalize transition-colors ${form.sports.includes(s) ? "bg-brand-green/10 border-brand-green/40 text-brand-green" : "border-white/10 text-gray-400 hover:text-white"}`}
                aria-pressed={form.sports.includes(s)}>{s}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5" htmlFor="city">City</label>
          <input id="city" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
            className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 dark-section"
            data-testid="organizer-city-input" />
        </div>
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5" htmlFor="experience">Organising Experience</label>
          <textarea id="experience" rows={2} required value={form.experience} onChange={e => setForm(p => ({ ...p, experience: e.target.value }))}
            placeholder="Club captain for 3 years, USTA coordinator…"
            className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green/40 resize-none dark-section"
            data-testid="organizer-experience-input" />
        </div>
        <button type="submit" disabled={loading}
          className="w-full py-3 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors disabled:opacity-50"
          data-testid="organizer-apply-btn">
          {loading ? <span className="flex items-center justify-center gap-2"><Loader size={14} className="animate-spin" /> Submitting…</span> : "Submit Application"}
        </button>
      </form>
    </div>
  );
}

// ── Create Session Modal ────────────────────────────────────────────────────
function CreateSessionModal({ onCreated, onClose }) {
  const [form, setForm] = useState({
    title: "", sport: "tennis", session_type: "open_play", date_time: "",
    location: "", skill_min: "beginner", skill_max: "advanced",
    capacity: 8, price: 0, description: "", city: "Fort Worth, TX",
    is_public: true, is_recurring: false, gender_preference: "open",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await fetch(`${API}/organizer/sessions`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(form),
      });
      if (r.ok) { onCreated(); onClose(); }
      else { const d = await r.json(); setError(d.detail || "Failed to create"); }
    } catch { setError("Network error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Create session">
      <div className="bg-[#161B28] border border-white/15 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 dark-section">
        <h2 className="text-lg font-bold text-white mb-4">Create Session</h2>
        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
        <form onSubmit={submit} className="space-y-4">
          <div><label className="text-xs text-gray-400 uppercase tracking-wider block mb-1" htmlFor="title">Title</label>
            <input id="title" required value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))}
              className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 dark-section"
              data-testid="session-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label htmlFor="Sport" className="text-xs text-gray-400 block mb-1">Sport</label><select id="Sport" value={form.sport} onChange={e => setForm(p => ({...p, sport: e.target.value}))}
                className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none dark-section">
                {SPORTS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}</select></div>
            <div><label htmlFor="Format" className="text-xs text-gray-400 block mb-1">Format</label><select id="Format" value={form.session_type} onChange={e => setForm(p => ({...p, session_type: e.target.value}))}
                className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none dark-section">
                {SESSION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
          </div>
          <div><label htmlFor="Date-Time" className="text-xs text-gray-400 block mb-1">Date & Time</label><input id="Date-Time" required value={form.date_time} onChange={e => setForm(p => ({...p, date_time: e.target.value}))}
              placeholder="Saturday Jan 18 @ 10am" className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 dark-section"
              data-testid="session-datetime-input" /></div>
          <div><label htmlFor="Location" className="text-xs text-gray-400 block mb-1">Location</label><input id="Location" required value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))}
              className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-green/40 dark-section" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label htmlFor="Skill-Min" className="text-xs text-gray-400 block mb-1">Skill Min</label><select id="Skill-Min" value={form.skill_min} onChange={e => setForm(p => ({...p, skill_min: e.target.value}))}
                className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none dark-section">
                {SKILLS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}</select></div>
            <div><label htmlFor="Skill-Max" className="text-xs text-gray-400 block mb-1">Skill Max</label><select id="Skill-Max" value={form.skill_max} onChange={e => setForm(p => ({...p, skill_max: e.target.value}))}
                className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none dark-section">
                {SKILLS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label htmlFor="Capacity" className="text-xs text-gray-400 block mb-1">Capacity</label><input id="Capacity" type="number" min={2} max={100} value={form.capacity} onChange={e => setForm(p => ({...p, capacity: +e.target.value}))}
                className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none dark-section" data-testid="session-capacity-input" /></div>
            <div><label htmlFor="Price" className="text-xs text-gray-400 block mb-1">Price ($)</label><input id="Price" type="number" min={0} step={0.01} value={form.price} onChange={e => setForm(p => ({...p, price: +e.target.value}))}
                className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none dark-section" data-testid="session-price-input" /></div>
          </div>
          <div><label htmlFor="Description-Optional" className="text-xs text-gray-400 block mb-1">Description (optional)</label><textarea id="Description-Optional" rows={2} value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}
              className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none resize-none dark-section" /></div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 text-sm border border-white/10 text-gray-400 rounded-xl hover:border-white/20 transition-colors">Cancel</button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 text-sm font-bold bg-brand-orange text-white rounded-xl hover:bg-brand-orange/90 transition-colors disabled:opacity-50"
              data-testid="create-session-submit-btn">
              {loading ? "Creating…" : "Create Session"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Organizer Dashboard ────────────────────────────────────────────────
export default function OrganizerDashboardPage() {
  const { user } = useAuth();
  const [orgStatus, setOrgStatus] = useState(null); // null|pending|approved|not_applied
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [applied, setApplied] = useState(false);

  const checkStatus = async () => {
    try {
      const r = await fetch(`${API}/organizers/me`, { credentials: "include" });
      if (r.ok) { const d = await r.json(); setOrgStatus(d.status); }
      else setOrgStatus("not_applied");
    } catch { setOrgStatus("not_applied"); }
  };

  const loadSessions = async () => {
    try {
      const r = await fetch(`${API}/organizer/sessions`, { credentials: "include" });
      if (r.ok) { const d = await r.json(); setSessions(d.sessions || []); }
    } catch {}
  };

  useEffect(() => {
    (async () => {
      await checkStatus();
      if (user?.role === "organizer" || user?.role === "admin") await loadSessions();
      setLoading(false);
    })();
  }, [user]);

  if (!user) return <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center dark-section"><p className="text-gray-400">Please <Link to="/login" className="text-brand-green hover:underline">log in</Link> to access the organizer portal.</p></div>;

  if (loading) return <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center dark-section"><Loader size={28} className="text-brand-green animate-spin" /></div>;

  // Not applied yet (admins bypass — they have organizer privileges)
  if (orgStatus === "not_applied" && !applied && user?.role !== "admin") return (
    <div className="min-h-screen bg-[#0F1629] pt-24 pb-16 px-4 dark-section">
      <OrganizerApply onApplied={() => { setApplied(true); setOrgStatus("pending"); }} />
    </div>
  );

  // Pending approval (admins bypass)
  if ((orgStatus === "pending" || applied) && user?.role !== "admin") return (
    <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center px-4 dark-section" data-testid="organizer-pending-page">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-2xl bg-yellow-400/10 border border-yellow-400/20 flex items-center justify-center mx-auto mb-5">
          <Clock size={28} className="text-yellow-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Application Under Review</h1>
        <p className="text-gray-400 text-sm mb-6">The ActivitySports team will review your application and approve within 1–2 business days.</p>
        <Link to="/find-partners" className="inline-flex items-center gap-2 text-brand-green hover:underline text-sm">Browse Partners <ChevronRight size={14} /></Link>
      </div>
    </div>
  );

  // Approved organizer dashboard
  return (
    <div className="min-h-screen bg-[#0F1629] pt-20 pb-16 dark-section" data-testid="organizer-dashboard">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-heading font-black text-white">Organizer Dashboard</h1>
            <p className="text-gray-500 text-sm mt-0.5">Create and manage your sessions</p>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange/90 transition-colors text-sm"
            data-testid="create-session-btn">
            <Plus size={16} /> New Session
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-[#161B28] border border-white/8 rounded-xl p-4 dark-section">
            <p className="text-xs text-gray-500 mb-1">Total Sessions</p>
            <p className="text-2xl font-bold text-white">{sessions.length}</p>
          </div>
          <div className="bg-[#161B28] border border-white/8 rounded-xl p-4 dark-section">
            <p className="text-xs text-gray-500 mb-1">Open</p>
            <p className="text-2xl font-bold text-brand-green">{sessions.filter(s => s.status === "open").length}</p>
          </div>
          <div className="bg-[#161B28] border border-white/8 rounded-xl p-4 dark-section">
            <p className="text-xs text-gray-500 mb-1">Completed</p>
            <p className="text-2xl font-bold text-white">{sessions.filter(s => s.status === "completed").length}</p>
          </div>
          <div className="bg-[#161B28] border border-white/8 rounded-xl p-4 dark-section">
            <p className="text-xs text-gray-500 mb-1">Total Participants</p>
            <p className="text-2xl font-bold text-white">{sessions.reduce((a, s) => a + (s.participants_count || 0), 0)}</p>
          </div>
        </div>

        {/* Sessions list */}
        {sessions.length === 0 ? (
          <div className="text-center py-16 bg-[#161B28] border border-white/8 rounded-2xl dark-section">
            <Calendar size={32} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 mb-4">No sessions yet. Create your first one!</p>
            <button onClick={() => setShowCreate(true)}
              className="px-5 py-2.5 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors text-sm"
              data-testid="create-first-session-btn">Create Session</button>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map(s => (
              <div key={s.id} className="bg-[#161B28] border border-white/8 rounded-xl p-4 flex items-center gap-4 dark-section" data-testid="organizer-session-item">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{s.title}</p>
                  <p className="text-xs text-gray-400">{s.date_time} · {s.location}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-gray-400"><Users size={12} className="inline mr-1" />{s.participants_count}/{s.capacity}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${s.status === "open" ? "bg-brand-green/10 text-brand-green border-brand-green/30" : s.status === "completed" ? "bg-white/5 text-gray-400 border-white/10" : "bg-yellow-400/10 text-yellow-400 border-yellow-400/30"}`}>{s.status}</span>
                  <Link to={`/organizer/sessions/${s.id}`} className="text-xs text-brand-green hover:underline">Manage</Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {showCreate && <CreateSessionModal onCreated={loadSessions} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
